"use client";

import { Box, Flex } from "@chakra-ui/react";
import { TouchAwareTooltip } from "@/components/ui";
import { Link } from "@/components";
import type { LinkItem } from "@/components/shared/sidebar/linkItems";
import {
  SidebarNavIcon,
  SidebarNavItemLabel,
  sidebarNavRowAlign,
} from "@/components/shared/sidebar/SidebarNavItemLabel";
import {
  SidebarNavCountBadge,
} from "@/components/shared/sidebar/SidebarNavCountBadge";
import {
  buildMakeupNavBadgeLabel,
  isMakeupRequestsNavHref,
  type MakeupNavBadge,
} from "@/components/shared/sidebar/makeupNavBadge";
import {
  buildUserTasksNavBadgeLabel,
  isMyTasksNavHref,
  type UserTasksNavBadge,
} from "@/components/shared/sidebar/userTasksNavBadge";
import type { useThemeTokens } from "@/lib/theme/tokens";

interface SidebarNavLinkItemProps {
  item: LinkItem;
  isCollapsed: boolean;
  isMobile?: boolean;
  onNavigate?: () => void;
  controlBorderRadius: string;
  isClassic: boolean;
  theme: ReturnType<typeof useThemeTokens>;
  makeupNavBadge?: MakeupNavBadge | null;
  userTasksNavBadge?: UserTasksNavBadge | null;
}

export function SidebarNavLinkItem({
  item,
  isCollapsed,
  isMobile = false,
  onNavigate,
  controlBorderRadius,
  isClassic,
  theme,
  makeupNavBadge = null,
  userTasksNavBadge = null,
}: SidebarNavLinkItemProps) {
  const isActive = item.active;
  const showMakeupBadge =
    isMakeupRequestsNavHref(item.href) && (makeupNavBadge?.count ?? 0) > 0;
  const showUserTasksBadge =
    isMyTasksNavHref(item.href) && (userTasksNavBadge?.count ?? 0) > 0;
  const tooltipLabel =
    showMakeupBadge && makeupNavBadge
      ? buildMakeupNavBadgeLabel(item.label, makeupNavBadge)
      : showUserTasksBadge && userTasksNavBadge
        ? buildUserTasksNavBadgeLabel(item.label, userTasksNavBadge)
        : item.label;

  const linkContent = (
    <Link
      href={item.href}
      onClick={isMobile ? onNavigate : undefined}
      display={isCollapsed ? "flex" : "block"}
      alignItems="center"
      justifyContent="center"
      w={isCollapsed ? undefined : "100%"}
      style={{
        transition: "all 0.2s linear",
      }}
    >
      <Flex
        align={sidebarNavRowAlign(isCollapsed)}
        justify={isCollapsed ? "center" : "flex-start"}
        px={isCollapsed ? 2 : 3}
        py={2.5}
        w={isCollapsed ? "40px" : "100%"}
        maxW="100%"
        borderRadius={controlBorderRadius}
        gap={isCollapsed ? 0 : 3}
        bg={isActive ? theme.accent.primaryBg : "transparent"}
        color={isActive ? theme.accent.primary : theme.text.secondary}
        transition="all 0.18s ease-out"
        sx={{
          "@media (hover: hover)": {
            "&:hover": {
              bg:
                theme.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(11,13,16,0.08)",
              color: isActive ? theme.accent.primary : theme.text.primary,
              transform: isCollapsed ? "scale(1.05)" : "translateX(2px)",
            },
          },
        }}
        position="relative"
        boxShadow={
          isActive
            ? isClassic
              ? "none"
              : theme.mode === "dark"
                ? "none"
                : "0 0 0 rgba(255,255,255,0.65), 0 4px 12px rgba(11,13,16,0.15)"
            : "none"
        }
      >
        <Box position="relative" flexShrink={0}>
          <SidebarNavIcon as={item.icon} />
          {isCollapsed && showMakeupBadge && makeupNavBadge ? (
            <SidebarNavCountBadge
              count={makeupNavBadge.count}
              variant="overlay"
              data-testid="sidebar-makeup-requests-badge"
            />
          ) : null}
          {isCollapsed && showUserTasksBadge && userTasksNavBadge ? (
            <SidebarNavCountBadge
              count={userTasksNavBadge.count}
              variant="overlay"
              data-testid="sidebar-user-tasks-badge"
            />
          ) : null}
        </Box>
        {!isCollapsed ? (
          <>
            <SidebarNavItemLabel flex="1" minW={0}>
              {item.label}
            </SidebarNavItemLabel>
            {showMakeupBadge && makeupNavBadge ? (
              <SidebarNavCountBadge
                count={makeupNavBadge.count}
                data-testid="sidebar-makeup-requests-badge"
              />
            ) : null}
            {showUserTasksBadge && userTasksNavBadge ? (
              <SidebarNavCountBadge
                count={userTasksNavBadge.count}
                data-testid="sidebar-user-tasks-badge"
              />
            ) : null}
          </>
        ) : (
          <SidebarNavItemLabel isCollapsed>
            {item.label}
          </SidebarNavItemLabel>
        )}
      </Flex>
    </Link>
  );

  if (isCollapsed) {
    return (
      <TouchAwareTooltip label={tooltipLabel} placement="right">
        {linkContent}
      </TouchAwareTooltip>
    );
  }

  return linkContent;
}
